import React, { forwardRef, ComponentProps } from 'react';
import { Col, Grid, Row, FlexboxGrid } from 'rsuite';

// RSuite's Col hard-codes role="gridcell", Row hard-codes role="row", and
// Grid hard-codes role="grid". When these are used as visual layout
// outside a genuine role="grid" hierarchy, axe-core's
// aria-required-parent / aria-required-children rules fire. Use these
// wrappers for layout; reach for raw rsuite Col/Row/FlexboxGrid only
// when the content is genuinely tabular and lives under a <Grid>.
//
// role="none" is the ARIA 1.1+ preferred spelling (equivalent to the
// older "presentation"). User-supplied `role` still wins — spread after
// the default so callers can opt back into grid semantics where
// appropriate.

export const PresentationCol = forwardRef<
  HTMLDivElement,
  ComponentProps<typeof Col>
>((props, ref) => (
  <Col
    ref={ref}
    role="none"
    {...props}
  />
));
PresentationCol.displayName = 'PresentationCol';

export const PresentationRow = forwardRef<
  HTMLDivElement,
  ComponentProps<typeof Row>
>((props, ref) => (
  <Row
    ref={ref}
    role="none"
    {...props}
  />
));
PresentationRow.displayName = 'PresentationRow';

export const PresentationGrid = forwardRef<
  HTMLDivElement,
  ComponentProps<typeof Grid>
>((props, ref) => (
  <Grid
    ref={ref}
    role="none"
    {...props}
  />
));
PresentationGrid.displayName = 'PresentationGrid';

// FlexboxGrid.Item passes `role` through to its `as` target. Render as
// a plain rsuite Col with role="none" directly — skips the extra
// PresentationCol layer that would just re-set the same role.
export const PresentationFlexboxGridItem = forwardRef<
  HTMLDivElement,
  ComponentProps<typeof FlexboxGrid.Item>
>((props, ref) => (
  <FlexboxGrid.Item
    ref={ref}
    as={Col}
    role="none"
    {...props}
  />
));
PresentationFlexboxGridItem.displayName = 'PresentationFlexboxGridItem';
